import { Alert, Box, Button, Stack } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCountryConfig } from "../../hooks/useCountryConfig";
import { useI18n } from "../../hooks/useI18n";
import { PageLayout } from "../../layout/PageLayout";
import { EvcCertificateSummary } from "./evc-import/EvcCertificateSummary";
import { ExistingHealthRecordSection } from "./evc-import/ExistingHealthRecordSection";
import { hasEvcIdentityMismatch } from "./evc-import/identity";
import { ImportDoneView, ImportErrorView, ImportingView, LoadingView } from "./evc-import/ImportStatusViews";
import { ImportTargetSection } from "./evc-import/ImportTargetSection";
import { NewHealthRecordDraftForm } from "./evc-import/NewHealthRecordDraftForm";
import { useEvcImportDraft } from "./evc-import/useEvcImportDraft";
import { useEvcImportSubmit } from "./evc-import/useEvcImportSubmit";
import { useEvcVaccinationSelection } from "./evc-import/useEvcVaccinationSelection";
import { useExistingHealthRecordTarget } from "./evc-import/useExistingHealthRecordTarget";
import { useImportDestination } from "./evc-import/useImportDestination";
import { VaccinationSelectionAccordion } from "./evc-import/VaccinationSelectionAccordion";

export function EvcImportPage() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { zipCode: zipCodeConfig } = useCountryConfig();
  const evcToken = useMemo(() => new URLSearchParams(location.search).get("evc"), [location.search]);
  const draft = useEvcImportDraft(evcToken);
  const { importDestinations, selectedDestination, selectedDestinationId, setSelectedDestinationId } =
    useImportDestination();
  const [targetMode, setTargetMode] = useState<"new" | "existing">("new");
  const [invalidGender, setInvalidGender] = useState(false);
  const [invalidZipCode, setInvalidZipCode] = useState(false);
  const genderRef = useRef<HTMLDivElement>(null);
  const zipCodeRef = useRef<HTMLDivElement>(null);
  const isFormActive = draft.status === "ready";

  const existingTarget = useExistingHealthRecordTarget({
    isActive: isFormActive,
    targetMode,
    selectedDestination,
  });

  const vaccinationSelection = useEvcVaccinationSelection({
    vaccinations: draft.vaccinations,
    targetMode,
    selectedExistingHealthRecord: existingTarget.selectedExistingHealthRecord,
  });

  const needsZipCode = zipCodeConfig.requiredForHealthRecord;
  const validateZipCode = useCallback(
    (value: string): boolean => {
      if (!needsZipCode && value.trim() === "") return true;
      return !!value.match(zipCodeConfig.regex);
    },
    [needsZipCode, zipCodeConfig.regex],
  );

  const importSubmit = useEvcImportSubmit({
    targetMode,
    selectedDestination,
    importDestinations,
    selectedExistingHealthRecord: existingTarget.selectedExistingHealthRecord,
    selectedVaccinationIds: vaccinationSelection.selectedVaccinationIds,
    vaccinations: draft.vaccinations,
    draft: {
      firstName: draft.firstName,
      lastName: draft.lastName,
      birthDate: draft.birthDate,
      gender: draft.gender,
      zipCode: draft.zipCode,
    },
    needsZipCode,
    validateZipCode,
    genderRef,
    zipCodeRef,
    setInvalidGender,
    setInvalidZipCode,
    markSelectedHealthRecordRequired: existingTarget.markSelectedHealthRecordRequired,
  });

  useEffect(() => {
    if (!evcToken && draft.status === "loading") {
      navigate("..", { relative: "path", replace: true });
    }
  }, [draft.status, evcToken, navigate]);

  const handleSkip = useCallback(() => {
    navigate("..", { relative: "path", replace: true });
  }, [navigate]);

  const handleDestinationChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setSelectedDestinationId(event.target.value);
    },
    [setSelectedDestinationId],
  );

  const handleTargetModeChange = useCallback(
    (nextTargetMode: "new" | "existing") => {
      setTargetMode(nextTargetMode);
      existingTarget.clearSelectedHealthRecordErrors();
    },
    [existingTarget],
  );

  const handleSelectedHealthRecordChange = useCallback(
    (healthRecordId: string | null) => {
      existingTarget.setSelectedExistingHealthRecordId(healthRecordId);
      existingTarget.clearSelectedHealthRecordErrors();
    },
    [existingTarget],
  );

  const evcFullName = draft.parsedEvc ? `${draft.parsedEvc.name.firstName} ${draft.parsedEvc.name.lastName}` : null;
  const evcDateOfBirth = draft.parsedEvc?.dateOfBirth.toISOString().slice(0, 10) ?? null;
  const hasIdentityMismatch = hasEvcIdentityMismatch(
    evcFullName,
    evcDateOfBirth,
    existingTarget.selectedExistingHealthRecord,
  );
  const submitDisabled =
    vaccinationSelection.selectedCount === 0 ||
    !selectedDestination ||
    (targetMode === "existing" &&
      (!existingTarget.selectedExistingHealthRecord ||
        existingTarget.selectedHealthRecordLoading ||
        existingTarget.selectedHealthRecordLoadError));

  return (
    <PageLayout title={t("evc_onboarding.title")}>
      {draft.status === "loading" && importSubmit.status === "idle" && <LoadingView />}

      {draft.status === "error" && importSubmit.status === "idle" && (
        <ImportErrorView errorMessage={draft.parseError} onSkip={handleSkip} />
      )}

      {importSubmit.status === "error" && (
        <ImportErrorView errorMessage={importSubmit.errorMessage} onSkip={handleSkip} />
      )}

      {isFormActive && importSubmit.status === "idle" && draft.parsedEvc && (
        <Stack spacing={3}>
          <Alert severity="info">{t("evc_onboarding.welcome_message")}</Alert>

          <EvcCertificateSummary
            parsedEvc={draft.parsedEvc}
            evcFullName={evcFullName}
            vaccinationsCount={draft.vaccinations.length}
          />

          <ImportTargetSection
            targetMode={targetMode}
            onTargetModeChange={handleTargetModeChange}
            importDestinations={importDestinations}
            selectedDestinationId={selectedDestinationId}
            onDestinationChange={handleDestinationChange}
          />

          {targetMode === "existing" && (
            <ExistingHealthRecordSection
              selectedDestination={selectedDestination}
              healthRecordOptions={existingTarget.healthRecordOptions}
              healthRecordOptionsLoading={existingTarget.healthRecordOptionsLoading}
              healthRecordOptionsError={existingTarget.healthRecordOptionsError}
              selectedExistingHealthRecord={existingTarget.selectedExistingHealthRecord}
              selectedHealthRecordLoading={existingTarget.selectedHealthRecordLoading}
              selectedHealthRecordRequiredError={existingTarget.selectedHealthRecordRequiredError}
              selectedHealthRecordLoadError={existingTarget.selectedHealthRecordLoadError}
              matchStats={vaccinationSelection.matchStats}
              hasIdentityMismatch={hasIdentityMismatch}
              onSelectedHealthRecordChange={handleSelectedHealthRecordChange}
              onSearchChange={existingTarget.setHealthRecordSearchValue}
            />
          )}

          {targetMode === "new" && (
            <NewHealthRecordDraftForm
              draft={{
                firstName: draft.firstName,
                lastName: draft.lastName,
                birthDate: draft.birthDate,
                gender: draft.gender,
                zipCode: draft.zipCode,
              }}
              setFirstName={draft.setFirstName}
              setLastName={draft.setLastName}
              setGender={draft.setGender}
              setZipCode={draft.setZipCode}
              needsZipCode={needsZipCode}
              zipCodePlaceholder={zipCodeConfig.placeholder}
              validateZipCode={validateZipCode}
              invalidGender={invalidGender}
              setInvalidGender={setInvalidGender}
              invalidZipCode={invalidZipCode}
              setInvalidZipCode={setInvalidZipCode}
              genderRef={genderRef}
              zipCodeRef={zipCodeRef}
            />
          )}

          <VaccinationSelectionAccordion
            vaccinations={draft.vaccinations}
            selectedCount={vaccinationSelection.selectedCount}
            targetMode={targetMode}
            selectedVaccinationIds={vaccinationSelection.selectedVaccinationIds}
            reconciledVaccinationById={vaccinationSelection.reconciledVaccinationById}
            onToggleVaccination={vaccinationSelection.toggleVaccination}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "flex-end" },
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button variant="outlined" onClick={handleSkip} sx={{ alignSelf: { xs: "stretch", md: "flex-end" } }}>
              {t("evc_onboarding.skip")}
            </Button>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: { xs: "stretch", md: "flex-end" },
                flex: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={importSubmit.submit}
                disabled={submitDisabled}
                disableElevation
                sx={{ alignSelf: { xs: "stretch", md: "flex-end" } }}
              >
                {t(targetMode === "existing" ? "evc_onboarding.add_selected" : "evc_onboarding.import_selected", {
                  count: vaccinationSelection.selectedCount,
                })}
              </Button>
            </Box>
          </Box>
        </Stack>
      )}

      {importSubmit.status === "importing" && <ImportingView importProgress={importSubmit.importProgress} />}

      {importSubmit.status === "done" && (
        <ImportDoneView
          importProgress={importSubmit.importProgress}
          viewRecordPath={importSubmit.buildViewRecordPath()}
        />
      )}
    </PageLayout>
  );
}
